import { inject } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import { useApi } from '@/plugins/api';
import { isArray, isObject, trim } from 'lodash-es';

const LOGGER_TAG = 'translation-store';
const locales = {
  it: () => import('dayjs/locale/it'),
  en: () => import('dayjs/locale/en'),
  fr: () => import('dayjs/locale/fr'),
  de: () => import('dayjs/locale/de'),
  es: () => import('dayjs/locale/es'),
}

export const useStoreTranslation = defineStore('translation', () => {

  let api = useApi();
  const logger = inject('logger');
  const { setLocaleMessage, t, locale, i18n, getLocaleMessage} = useI18n();

  async function setDayjsLocale() {
    let localePath = 'dayjs/locale/' + locale.value;
    logger.info(LOGGER_TAG, 'setting dayjs locale', localePath);
    await locales[locale.value]();
    dayjs.locale(locale.value);
  }

  async function getLang() {
    try {
      let { data } = await api.get('/customer/get_lang');
      let dictionary = data.dictionary;
      const existing = getLocaleMessage(data.locale)
      const merged = {
        ...existing,
        ...dictionary
      }
      setLocaleMessage(data.locale, merged);
      locale.value = data.locale;
      setDayjsLocale();
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function setLang(lang) {
    try {
      let { data } = await api.post('/customer/act/set-lang', { lang });

      let { done } = data;

      if (!done) {
        return false;
      }

      await getLang(lang);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  function isTranslatable(key) {
    // se la stringa inizia con $ allora traducila allora ritorna true
    if (!key) {
      return false;
    }

    if (key[0] === '$') {
      return true
    }

    return false
  }

  /**
   * Sostituisce tutte le occorrenze di %s con i valori in sequenza di params
   * @param {string} string - La stringa contenente i segnaposto %s
   * @param {Object} params - Un oggetto con i valori da sostituire, es: { s1: 'val1', s2: 'val2' }
   * @returns {string} - La stringa con i segnaposto sostituiti dai valori dei parametri
   */
  function serverInterpolation(string, params) {
    let index = 1; // Indicatore per la sequenza dei segnaposto
    return string.replace(/%s/g, () => {
      const key = `s${index}`; // Costruisce la chiave nell'ordine s1, s2, ...
      const value = params[key]; // Prende il valore dall'oggetto params
      index++; // Incrementa l'indicatore
      return `${value}`; // Ritorna il segnaposto formattato
    });
  }

  function needsServerInterpolation(string) {
    return string.includes('%s');
  }


  function translate(key, params = {}) {
    if (!key) {
      return key;
    }

    if (isArray(key)) {
      let result = [];
      for (let i = 0; i < key.length; i++) {
        result.push(translate(trim(key[i]), params));
      }
      return result.join(' - ');
    }

    if (isObject(key)) {
      return translate(key.key, key.params);
    }

    if (isTranslatable(key)) {
      return t(key.slice(1), params);
    }

    let translatedString = t(key, params);

    if (needsServerInterpolation(translatedString)) {
      translatedString = serverInterpolation(translatedString, params);
    }

    return translatedString;
  }

  return {
    getLang, setLang, isTranslatable, translate
  }
});